import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faSpinner,
  faExclamationTriangle,
  faTrophy,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { powerStatsAPI, baseURL, CURRENT_SEASON, AVAILABLE_SEASONS } from '../../Constants';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/admin/admin-tournaments-page.scss';

// Define interfaces based on the backend models
interface Team {
  id: string;
  name: string;
  logo: string | null;
  division?: string | null;
  seed?: string | null;
  players: any[];
}

interface UpcomingMatch {
  challongeId: string;
  team1: Team;
  team2: Team;
  isPlayoff: boolean;
  playoffPlayed: boolean;
  division: string;
  matchTime: string;
  streamUrl: string;
  veto?: string | null;
  bracketPosition?: number | null;
  season?: number; // Add season property to match API response
  hidden?: boolean; // Add hidden field
}

interface Match {
  vetoLog?: string | null;
  gameId: string;
  matchId: string;
  mapName: string;
  season: number;
  division: string;
  team1: Team;
  team2: Team;
  team1PlayerIds: string[];
  team2PlayerIds: string[];
  team1Score: number;
  team2Score: number;
  team1Won: boolean;
  bo3: boolean;
  playoff: boolean;
  bracketPosition?: number | null;
  matchTime: string;
  roundEnds: Record<string, any>;
}

interface AllMatches {
  upcomingMatches: UpcomingMatch[];
  playedMatches: Match[];
}

// Shape of the raw API response
interface ApiUpcomingMatch {
  challonge_id: string;
  team1: {
    teamId: string;
    teamName: string;
    teamLogo: string | null;
    division?: string | null;
    seed?: string | null;
    ownerId: string | null;
    // ... other properties
  };
  team2: {
    teamId: string;
    teamName: string;
    teamLogo: string | null;
    division?: string | null;
    seed?: string | null;
    ownerId: string | null;
    // ... other properties
  };
  isPlayoff: boolean;
  playoffPlayed: boolean;
  division: string;
  match_time: string;
  stream_url: string;
  veto?: string | null;
  roundNumber?: number | null;
  season?: number; // Add season property to match API response
}

interface ApiResponse {
  upcomingMatches: ApiUpcomingMatch[];
  playedMatches: any[]; // Define this more specifically if needed
}

const AdminTournamentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useContext(AuthContext);
  const [matches, setMatches] = useState<AllMatches | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [season, setSeason] = useState<number>(CURRENT_SEASON); // Default to current season
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>(AVAILABLE_SEASONS);
  // A better placeholder image matching the site's design system
  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
  
  // Check if user is an admin before allowing access
  useEffect(() => {
    if (!token || !isAdmin) {
      navigate('/admin/login');
    }
  }, [token, isAdmin, navigate]);

  // Transform API response to match our interface
  const transformApiResponse = (data: ApiResponse): AllMatches => {
    
    return {
      upcomingMatches: data.upcomingMatches?.map(match => ({
        challongeId: match.challonge_id,
        team1: {
          id: match.team1.teamId,
          name: match.team1.teamName,
          logo: match.team1.teamLogo,
          division: match.team1.division,
          seed: match.team1.seed,
          players: []
        },
        team2: {
          id: match.team2.teamId,
          name: match.team2.teamName,
          logo: match.team2.teamLogo,
          division: match.team2.division,
          seed: match.team2.seed,
          players: []
        },
        isPlayoff: match.isPlayoff,
        playoffPlayed: match.playoffPlayed,
        division: match.division,
        matchTime: match.match_time,
        streamUrl: match.stream_url,
        veto: match.veto,
        bracketPosition: match.roundNumber,
        season: match.season // Include season from the API response
      })) || [],
      playedMatches: data.playedMatches?.map(match => {
        // Log each match for debugging
        
        // Extract team info correctly for played matches
        const team1 = {
          id: match.team1?.teamId || match.team1?.id || "",
          name: match.team1?.teamName || match.team1?.name || "Unknown Team",
          logo: match.team1?.teamLogo || match.team1?.logo || null,
          // If match.division is null, use the team's division instead
          division: match.division || match.team1?.division || null,
          seed: match.team1?.seed || null,
          players: []
        };
        
        const team2 = {
          id: match.team2?.teamId || match.team2?.id || "",
          name: match.team2?.teamName || match.team2?.name || "Unknown Team",
          logo: match.team2?.teamLogo || match.team2?.logo || null,
          // If match.division is null, use the team's division instead
          division: match.division || match.team2?.division || null,
          seed: match.team2?.seed || null,
          players: []
        };
        
        // Handle all possible field name variations (camelCase vs lowercase)
        return {
          vetoLog: match.vetoLog || null,
          gameId: match.gameId || match.gameid || "",
          matchId: match.matchId || match.matchid || "",
          // For mapName, provide a default if null
          mapName: match.mapName || match.mapname || "Undetermined",
          season: match.season || 0,
          // If match.division is null, try to determine it from the teams
          division: match.division || 
                   (team1.division && team2.division ? 
                    (team1.division === team2.division ? team1.division : `Mixed (${team1.division}/${team2.division})`) : 
                    "Unknown"),
          team1: team1,
          team2: team2,
          team1PlayerIds: match.team1PlayerIds || match.team1playerids || [],
          team2PlayerIds: match.team2PlayerIds || match.team2playerids || [],
          team1Score: typeof match.team1Score !== 'undefined' ? match.team1Score : 
                     (typeof match.team1score !== 'undefined' ? match.team1score : 0),
          team2Score: typeof match.team2Score !== 'undefined' ? match.team2Score : 
                     (typeof match.team2score !== 'undefined' ? match.team2score : 0),
          team1Won: typeof match.team1Won !== 'undefined' ? match.team1Won : 
                   (typeof match.team1won !== 'undefined' ? match.team1won : false),
          bo3: typeof match.bo3 !== 'undefined' ? match.bo3 : false,
          playoff: typeof match.playoff !== 'undefined' ? match.playoff : false,
          bracketPosition: match.bracketPosition || match.roundNumber || null,
          matchTime: match.matchTime || match.match_time || new Date().toISOString(),
          roundEnds: match.roundEnds || {}
        };
      }) || []
    };
  };

  // Fetch matches data
  const fetchMatches = async () => {
    setIsLoading(true);
    setError(null);
    
    
    try {
      // Construct URL with query parameter instead of using a request body
      const url = `${powerStatsAPI}/Match/getAllMatches?season=${season}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
        // No body for GET request
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      // Log the raw text response for debugging
      const responseText = await response.text();
      
      // Parse the JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Error parsing JSON response:', e);
        throw new Error('Invalid JSON response from API');
      }
      
      
      // Ensure upcomingMatches exists and is an array
      if (!data.upcomingMatches) {
        data.upcomingMatches = [];
      }
      
      // Ensure playedMatches exists and is an array
      if (!data.playedMatches) {
        data.playedMatches = [];
      }
      
      // Filter the matches by season - this ensures we're only showing matches for the selected season
      if (data.upcomingMatches && Array.isArray(data.upcomingMatches)) {
        // Only keep matches for the current season
        data.upcomingMatches = data.upcomingMatches.filter((match: ApiUpcomingMatch) => 
          match.season === undefined || match.season === season
        );
      }
      
      const transformedData = transformApiResponse(data);
      
      setMatches(transformedData);
      
      // Extract available seasons from ALL matches (both upcoming and played)
      const seasonsFromUpcoming = data.upcomingMatches?.map((match: ApiUpcomingMatch) => match.season).filter(Boolean) || [];
      const seasonsFromPlayed = data.playedMatches?.map((match: any) => match.season).filter(Boolean) || [];

      // Combine and get unique seasons
      const allSeasons = [...seasonsFromUpcoming, ...seasonsFromPlayed];
      const seasonsSet = new Set<number>([...allSeasons, ...AVAILABLE_SEASONS]); // Always include configured seasons
      const seasons = Array.from(seasonsSet);
      seasons.sort((a: number, b: number) => b - a); // Sort descending
      
      setAvailableSeasons(seasons);
    } catch (err: unknown) {
      console.error('Error fetching matches:', err);
      setError(`Failed to load matches: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
    
    // Set page title
    document.title = 'Manage Tournaments - Northern Championships Series Admin';
  }, [season]); // Re-fetch when season changes

  // Format date and time
  const formatDateTime = (dateTimeString: string) => {
    try {
      // Handle empty or null
      if (!dateTimeString) {
        return 'No Date';
      }
      
      // Try to create a date - ensure it's not a timestamp
      let date: Date;
      if (typeof dateTimeString === 'number') {
        date = new Date(dateTimeString);
      } else if (typeof dateTimeString === 'string') {
        // Remove any 'T' or 'Z' to make parsing more reliable
        const cleanedDateString = dateTimeString
          .replace('T', ' ')
          .replace('Z', '');
        
        date = new Date(cleanedDateString);
      } else {
        date = new Date(dateTimeString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date format:', dateTimeString);
        return 'Invalid Date';
      }
      
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateTimeString);
      return 'Invalid Date';
    }
  };

  // Handle delete upcoming match
  const handleDeleteUpcomingMatch = async (challongeId: string) => {
    try {
      const response = await fetch(`${powerStatsAPI}/Match/deleteUpcomingMatch?matchId=${challongeId}`, {
        method: 'POST', // Change from DELETE to POST
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
  
      // Refresh matches
      fetchMatches();
      setConfirmingDelete(null);
    } catch (err: unknown) {
      console.error('Error deleting upcoming match:', err);
      setError('Failed to delete match. Please try again later.');
    }
  };

  // Handle season change
  const handleSeasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSeason = parseInt(e.target.value);
    setSeason(newSeason);
  };

  return (
    <div className="admin-tournaments-page">
      <div className="container">
        <div className="admin-header">
          <h1>Manage Tournaments</h1>
          <Link to="/admin/tournaments/create" className="btn-primary">
            <FontAwesomeIcon icon={faPlus} /> ADD NEW MATCH
          </Link>
        </div>

        {error && (
          <div className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}

        <div className="filter-controls">
          <div className="season-selector">
            <label htmlFor="seasonSelect">Season:</label>
            <select
              id="seasonSelect"
              value={season}
              onChange={handleSeasonChange}
            >
              {availableSeasons.map((s) => (
                <option key={s} value={s}>
                  Season {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="tournaments-panel">
          <div className="panel-section upcoming-matches">
            <h2><FontAwesomeIcon icon={faCalendarAlt} /> Upcoming Matches</h2>
            
            {isLoading ? (
              <div className="loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <p>Loading matches...</p>
              </div>
            ) : !matches || matches.upcomingMatches.length === 0 ? (
              <div className="no-matches">
                <p>No upcoming matches found for Season {season}. Click "Add New Match" to add one.</p>
              </div>
            ) : (
              <div className="matches-table">
                <div className="table-header">
                  <div className="header-time">DATE & TIME</div>
                  <div className="header-teams">TEAMS</div>
                  <div className="header-division">DIVISION</div>
                  <div className="header-type">TYPE</div>
                  <div className="header-actions">ACTIONS</div>
                </div>
                
                {matches.upcomingMatches.map((match, index) => (
                  <div key={`${match.challongeId}-${index}`} className="match-row">
                    <div className="match-time">
                      {formatDateTime(match.matchTime)}
                    </div>
                    <div className="match-teams">
                      <div className="team">
                        <img 
                          src={match.team1.logo || teamPlaceholderImage} 
                          alt={`${match.team1.name} logo`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                        <span>{match.team1.name}</span>
                      </div>
                      <div className="vs">VS</div>
                      <div className="team">
                        <img 
                          src={match.team2.logo || teamPlaceholderImage} 
                          alt={`${match.team2.name} logo`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                        <span>{match.team2.name}</span>
                      </div>
                    </div>
                    <div className="match-division">
                      {match.division}
                    </div>
                    <div className="match-type">
                      {match.isPlayoff ? 'Playoff' : 'Regular'}
                    </div>
                    <div className="match-actions">
                      <Link 
                        to={`/admin/tournaments/edit/${match.challongeId}`} 
                        className="action-link edit"
                        title="Edit match"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      
                      {confirmingDelete === match.challongeId ? (
                        <div className="delete-confirmation">
                          <span>Delete?</span>
                          <button 
                            className="btn-confirm-delete"
                            onClick={() => handleDeleteUpcomingMatch(match.challongeId)}
                          >
                            Yes
                          </button>
                          <button 
                            className="btn-cancel-delete"
                            onClick={() => setConfirmingDelete(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="action-link delete"
                          onClick={() => setConfirmingDelete(match.challongeId)}
                          title="Delete match"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="panel-section played-matches">
            <h2><FontAwesomeIcon icon={faTrophy} /> Played Matches</h2>
            
            {isLoading ? (
              <div className="loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <p>Loading matches...</p>
              </div>
            ) : !matches || matches.playedMatches.length === 0 ? (
              <div className="no-matches">
                <p>No played matches found for Season {season}.</p>
              </div>
            ) : (
              <div className="matches-table">
                <div className="table-header">
                  <div className="header-time">DATE & TIME</div>
                  <div className="header-teams">TEAMS</div>
                  <div className="header-score">SCORE</div>
                  <div className="header-map">MAP</div>
                  <div className="header-division">DIVISION</div>
                  <div className="header-type">TYPE</div>
                  <div className="header-actions">ACTIONS</div>
                </div>
                
                {matches.playedMatches.map((match, index) => (
                  <div key={`${match.matchId}-${index}`} className="match-row">
                    <div className="match-time">
                      {formatDateTime(match.matchTime)}
                    </div>
                    <div className="match-teams">
                      <div className={`team ${match.team1Won ? 'winner' : ''}`}>
                        <img 
                          src={match.team1.logo || teamPlaceholderImage} 
                          alt={`${match.team1.name} logo`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                        <span>{match.team1.name}</span>
                      </div>
                      <div className="vs">VS</div>
                      <div className={`team ${!match.team1Won ? 'winner' : ''}`}>
                        <img 
                          src={match.team2.logo || teamPlaceholderImage} 
                          alt={`${match.team2.name} logo`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                        <span>{match.team2.name}</span>
                      </div>
                    </div>
                    <div className="match-score">
                      <span className={match.team1Won ? 'winner' : ''}>{match.team1Score}</span>
                      <span>:</span>
                      <span className={!match.team1Won ? 'winner' : ''}>{match.team2Score}</span>
                    </div>
                    <div className="match-map">
                      {match.mapName}
                    </div>
                    <div className="match-division">
                      {match.division}
                    </div>
                    <div className="match-type">
                      {match.playoff ? 'Playoff' : 'Regular'}
                      {match.bo3 ? ' (BO3)' : ''}
                    </div>
                    <div className="match-actions">
                      <Link 
                        to={`/admin/tournaments/view/${match.matchId}`} 
                        className="action-link view"
                        title="View match details"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTournamentsPage;